import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Eye } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";

function PageForm({ page, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: page?.title || "", slug: page?.slug || "", content: page?.content || "",
    meta_description: page?.meta_description || "", is_published: page?.is_published ?? true,
    show_in_nav: page?.show_in_nav ?? false, nav_label: page?.nav_label || "", nav_order: page?.nav_order ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const inputCls = "w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (page?.id) await api.put(`/pages/${page.id}`, form);
      else await api.post("/pages/", form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-zinc-900 border border-white/5 rounded-xl p-5">
      {error && <div className="text-red-400 text-sm bg-red-900/20 px-3 py-2 rounded-md">{error}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Titel *</label>
          <input value={form.title} onChange={(e) => setForm(f => ({...f, title: e.target.value}))} required className={inputCls} />
        </div>
        <div>
          <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Slug</label>
          <input value={form.slug} onChange={(e) => setForm(f => ({...f, slug: e.target.value}))} className={inputCls} placeholder="auto-generiert" />
        </div>
      </div>
      <div>
        <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">HTML Content</label>
        <textarea value={form.content} onChange={(e) => setForm(f => ({...f, content: e.target.value}))}
          rows={10} className={`${inputCls} resize-y font-mono text-xs`} placeholder="<p>Inhalt...</p>" />
      </div>
      <div>
        <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Meta Description</label>
        <input value={form.meta_description} onChange={(e) => setForm(f => ({...f, meta_description: e.target.value}))} className={inputCls} />
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_published} onChange={(e) => setForm(f => ({...f, is_published: e.target.checked}))} className="w-4 h-4 rounded accent-rose-600" />
          <span className="text-zinc-300 text-sm">Veröffentlicht</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.show_in_nav} onChange={(e) => setForm(f => ({...f, show_in_nav: e.target.checked}))} className="w-4 h-4 rounded accent-rose-600" />
          <span className="text-zinc-300 text-sm">In Navigation anzeigen</span>
        </label>
      </div>
      {form.show_in_nav && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-zinc-400 text-xs block mb-1">Nav-Label</label>
            <input value={form.nav_label} onChange={(e) => setForm(f => ({...f, nav_label: e.target.value}))} className={inputCls} placeholder="Menü-Text" />
          </div>
          <div>
            <label className="text-zinc-400 text-xs block mb-1">Reihenfolge</label>
            <input type="number" value={form.nav_order} onChange={(e) => setForm(f => ({...f, nav_order: parseInt(e.target.value) || 0}))} className={inputCls} />
          </div>
        </div>
      )}
      <div className="flex gap-3">
        <button type="submit" disabled={saving} className="px-5 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold text-sm rounded-md transition-colors">
          {saving ? "Speichern..." : "Speichern"}
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-md transition-colors">Abbrechen</button>
      </div>
    </form>
  );
}

export default function PagesAdminPage() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPages = async () => {
    setLoading(true);
    try { const res = await api.get("/pages/"); setPages(res.data || []); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchPages(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Seite wirklich löschen?")) return;
    try { await api.delete(`/pages/${id}`); fetchPages(); }
    catch { alert("Fehler beim Löschen"); }
  };

  return (
    <AdminLayout>
      <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-black text-white font-heading">Seiten</h1>
        <button onClick={() => { setCreating(true); setEditing(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-md transition-colors">
          <Plus size={16} /> Neue Seite
        </button>
      </div>

      {creating && (
        <div className="mb-6">
          <PageForm onSave={() => { setCreating(false); fetchPages(); }} onCancel={() => setCreating(false)} />
        </div>
      )}

      {editing && (
        <div className="mb-6">
          <PageForm page={editing} onSave={() => { setEditing(null); fetchPages(); }} onCancel={() => setEditing(null)} />
        </div>
      )}

      <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full admin-table">
          <thead>
            <tr className="border-b border-white/5">
              <th className="px-4 py-3 text-left">Titel</th>
              <th className="px-4 py-3 text-left hidden sm:table-cell">Slug</th>
              <th className="px-4 py-3 text-left hidden md:table-cell">Status</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500 text-sm">Laden...</td></tr>
            ) : pages.map((p) => (
              <tr key={p.id} className="hover:bg-zinc-800/30 transition-colors">
                <td className="px-4 py-3 text-white text-sm font-medium">{p.title}</td>
                <td className="px-4 py-3 hidden sm:table-cell text-zinc-500 text-xs font-mono">/page/{p.slug}</td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${p.is_published ? "bg-green-900/30 text-green-400" : "bg-zinc-800 text-zinc-500"}`}>
                    {p.is_published ? "Veröffentlicht" : "Entwurf"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <a href={`/page/${p.slug}`} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded transition-colors">
                      <Eye size={15} />
                    </a>
                    <button onClick={() => { setEditing(p); setCreating(false); }}
                      className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded transition-colors">
                      <Edit size={15} />
                    </button>
                    <button onClick={() => handleDelete(p.id)}
                      className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </AdminLayout>
  );
}
